import joi from 'joi';
import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  List,
  Pagination,
  Skeleton,
  TextField,
  Theme,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Page } from '@qualifio/api-client';
import { PromocodeAttributes } from '@loyalty/shared-common/rewards-api-client';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map } from 'rxjs/operators';
import { faSearch } from '@fortawesome/pro-solid-svg-icons/faSearch';
import { faSparkles } from '@fortawesome/pro-solid-svg-icons/faSparkles';
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes';
import { makeStyles } from '@mui/styles';
import { useAlerts } from '@qualifio/ui-commons';
import { useTranslation } from 'react-i18next';
import { useJoiResolver } from '../../../shared/forms/use-joi-resolver';

interface Form {
  search: string;
}

interface SearchParams {
  search?: string;
  pageNumber: number;
}

const useStyles = makeStyles<Theme>(({ spacing, palette }) => ({
  promocodeModal: {
    width: 'auto',
  },
}));

function SearchBar({
  onChange,
  inputPlaceholder,
}: {
  onChange: (search: string) => void;
  inputPlaceholder: string;
}) {
  const resolver = useJoiResolver(
    joi.object<Form>({
      search: joi.string().optional().empty('').min(3),
    }),
  );
  const {
    handleSubmit,
    control,
    formState: { isValid, errors },
    watch,
  } = useForm<Form>({
    mode: 'onChange',
    resolver,
  });

  const search = watch('search');

  useEffect(() => {
    search$.next({ search, isValid });
  }, [search, isValid]);

  const { current: search$ } = useRef(new Subject<{ search: string; isValid: boolean }>());

  useEffect(() => {
    search$
      .pipe(
        debounceTime(300),
        distinctUntilChanged((a, b) => a.isValid === b.isValid && a.search === b.search),
        filter(({ isValid }) => isValid),
        map(({ search }) => search),
      )
      .subscribe(onChange);
  }, []);

  return (
    <form onSubmit={handleSubmit(({ search }) => onChange(search))}>
      <Controller
        name="search"
        control={control}
        defaultValue={''}
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <TextField
            {...field}
            inputRef={ref}
            error={!!error}
            helperText={error?.message}
            autoFocus
            autoComplete="off"
            placeholder={inputPlaceholder}
          />
        )}
      />
    </form>
  );
}

export function SearchPromocodes({
  doSearch,
  onDelete,
  inputPlaceholder,
  ItemComponent,
}: {
  doSearch: (pageNumber: number, search?: string) => Promise<Page<PromocodeAttributes> | undefined>;
  onDelete?: (promocode: PromocodeAttributes) => void;
  inputPlaceholder?: string;
  ItemComponent: FunctionComponent<{
    promocode: PromocodeAttributes;
    onDelete?: (code: PromocodeAttributes) => void;
  }>;
}) {
  const { t } = useTranslation();
  const { notify } = useAlerts();
  const classes = useStyles();
  const [open, setOpen] = useState(false);

  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState<Page<PromocodeAttributes>>();
  const [searchParams, setSearchParams] = useState<SearchParams>({ pageNumber: 1 });

  async function searchPromocodes(params: SearchParams): Promise<void> {
    try {
      setPage(await doSearch(params.pageNumber - 1, params.search));
      setLoading(false);
    } catch (error) {
      notify(t('rewards.promo-codes.errors.search'), 'error');
      console.warn(error);
      setLoading(false);
    }
  }

  const handleDelete = useCallback(
    (promocode: PromocodeAttributes) => {
      searchPromocodes(searchParams);
      onDelete?.(promocode);
    },
    [onDelete],
  );

  const handleClose = () => {
    setOpen(false);
    setLoading(false);
    setPage(undefined);
  };

  useEffect(() => {
    if (open) {
      searchPromocodes(searchParams);
    }
  }, [searchParams, open]);

  return (
    <>
      <Button
        color="secondary"
        startIcon={<FontAwesomeIcon icon={faSearch} />}
        onClick={() => setOpen(true)}
      >
        {t('rewards.promo-codes.button.search')}
      </Button>
      <Dialog
        className={classes.promocodeModal}
        onClose={handleClose}
        open={open}
        fullWidth={true}
        maxWidth="xl"
      >
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 15,
          }}
        >
          <FontAwesomeIcon icon={faTimes} />
        </IconButton>
        <DialogContent>
          <Box pb={2} display="block">
            <SearchBar
              onChange={(search: string) => {
                setSearchParams({ pageNumber: 1, search });
              }}
              inputPlaceholder={
                inputPlaceholder ?? t('rewards.campaign-redeemer.search.message.search')
              }
            />
          </Box>
          <List>
            {loading ? (
              <Skeleton />
            ) : !page?.data?.length ? (
              <Box textAlign="center" pt={2} pb={2}>
                <FontAwesomeIcon icon={faSparkles} size="3x" />
                <div>{t('rewards.promo-codes.search.message.empty-search')}</div>
              </Box>
            ) : (
              page.data.map(item => (
                <ItemComponent promocode={item} key={item.id} onDelete={handleDelete} />
              ))
            )}
          </List>
          {!!page?.data?.length && (
            <Box mt={2} display="flex" justifyContent="flex-end">
              <Pagination
                count={page.pageCount}
                page={searchParams.pageNumber}
                onChange={(event, n) =>
                  setSearchParams(prev => ({ search: prev.search, pageNumber: n }))
                }
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
